import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, ArrowLeft } from 'lucide-react';
import { discountService } from '../../services/discountService';
import { DISCOUNT_GROUPS, ALL_DISCOUNT_TYPES, isEnabled } from '../../config/discountTypes';

// =============================================================================
// Discount Configuration — choose which discount types appear on the Discounts
// & Trade Spend editing screen. Persists to md_discount_config (company-level).
// The catalogue itself is fixed in config/discountTypes.js; here we only toggle
// each type on/off. Disabled types never show on the editing grid.
// =============================================================================

const basisTag = (basis) => (basis === 'amt' ? 'Amount (R)' : '% of gross');

const DiscountConfig = () => {
  const navigate = useNavigate();
  const [enabled, setEnabled] = useState({}); // { code: bool } — full catalogue
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true); setError(null);
    try {
      const res = await discountService.getConfig();
      const map = {};
      (res.success ? res.data : []).forEach((r) => { map[r.discount_code] = r.is_enabled; });
      // Seed every catalogue type from stored value, else its default.
      setEnabled(Object.fromEntries(ALL_DISCOUNT_TYPES.map((t) => [t.code, isEnabled(map, t.code)])));
    } catch (e) {
      setError('Failed to load discount configuration.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const enabledCount = useMemo(() => Object.values(enabled).filter(Boolean).length, [enabled]);

  const setType = (code, on) => { setEnabled((prev) => ({ ...prev, [code]: on })); setSaved(false); };
  const setGroup = (group, on) => {
    setEnabled((prev) => {
      const next = { ...prev };
      group.types.forEach((t) => { next[t.code] = on; });
      return next;
    });
    setSaved(false);
  };

  const handleSave = async () => {
    setError(null); setIsSaving(true);
    try {
      const items = ALL_DISCOUNT_TYPES.map((t) => ({ discount_code: t.code, is_enabled: !!enabled[t.code] }));
      const res = await discountService.upsertConfig({ items });
      if (!res.success) throw new Error(res.message);
      setSaved(true);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save configuration.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto font-sans bg-slate-100 dark:bg-slate-900">
      <div className="max-w-4xl mx-auto px-6 py-10">
        <button type="button" onClick={() => navigate('/revenue/discounts')}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to Discounts
        </button>

        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <h1 className="m-0 text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Discount Configuration</h1>
            <p className="mt-2 text-base text-slate-500 dark:text-slate-400">
              Choose which discount types appear on the Discounts &amp; Trade Spend screen. Turn off the ones your business doesn't use to keep the editing grid focused.
            </p>
          </div>
          <button onClick={handleSave} disabled={isSaving}
            className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 shadow-sm">
            <Save className="w-4 h-4" /> {isSaving ? 'Saving…' : 'Save'}
          </button>
        </div>

        {error && <div className="mb-4 p-2.5 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md">{error}</div>}
        {saved && <div className="mb-4 p-2.5 bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm rounded-md">Saved. {enabledCount} discount type{enabledCount === 1 ? '' : 's'} enabled.</div>}

        {isLoading ? (
          <div className="p-10 text-center text-sm text-slate-400">Loading…</div>
        ) : (
          <div className="flex flex-col gap-5">
            {DISCOUNT_GROUPS.map((group) => {
              const on = group.types.filter((t) => enabled[t.code]).length;
              const allOn = on === group.types.length;
              return (
                <div key={group.code} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/95">
                    <div>
                      <h3 className="m-0 text-sm font-bold text-slate-800 dark:text-slate-100">{group.label}</h3>
                      <p className="m-0 mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                        {on}/{group.types.length} enabled{group.hiddenByDefault ? ' · off by default' : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => setGroup(group, true)}
                        className={`px-2.5 py-1 text-xs rounded border transition-colors ${allOn ? 'bg-slate-900 text-white border-slate-900 dark:bg-slate-200 dark:text-slate-900 dark:border-slate-200' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700'}`}>
                        All
                      </button>
                      <button type="button" onClick={() => setGroup(group, false)}
                        className={`px-2.5 py-1 text-xs rounded border transition-colors ${on === 0 ? 'bg-slate-900 text-white border-slate-900 dark:bg-slate-200 dark:text-slate-900 dark:border-slate-200' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700'}`}>
                        None
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 p-3">
                    {group.types.map((t) => (
                      <label key={t.code} className="flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer">
                        <input type="checkbox" className="accent-emerald-600 w-4 h-4" checked={!!enabled[t.code]} onChange={(e) => setType(t.code, e.target.checked)} />
                        <span className="flex-1 text-sm text-slate-700 dark:text-slate-200">{t.label}</span>
                        <span className="text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500">{basisTag(t.basis)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DiscountConfig;
